import { AriaTelemetryHandler, IAriaTelemetryHandlerParams } from './AriaTelemetryHandler';
import * as Guid from '@ms/utilities-guid/lib/index';

export type ISharedAriaEnvironment = 'ppe' | 'prod';
export type IAppEnvironment = 'ppe' | 'prod';

const ARIA_TOKEN = 'eeddb977348d43ebaaae45e759ea6e96-42528e91-0556-4879-9b8d-af7b3b8417e4-6984';

export type ISharedAriaTelemetryHandlerParams =
  Pick<IAriaTelemetryHandlerParams, 'context'> &
  Partial<Pick<IAriaTelemetryHandlerParams, 'ariaLoader'>> & {
    /**
     * Friendly name for the host application.
     */
    clientName: string;
    /**
     * Server-side or current activity correlation id to use as the base for all events.
     */
    correlationId?: string;
    /**
     * Environment identifier for the host application. For general production, use `prod`. For all others, use `ppe`.
     */
    environment?: IAppEnvironment;
    /**
     * Language in wich the app was loaded.
     */
    language?: string;
    /**
     * App-specific default sub-scenario for telemetry. Applies to all QoS and background events.
     */
    scenario?: string;
    /**
     * An auth token from which to extract user identity information.
     * This can be used to auto-populate `context` values when using OAuth.
     */
    userAuthToken?: string;

    /**
     * User ID - can be specified as an alternate user ID if the ID cannot be extracted from the token (for example, for ODC)
     */
    userId?: string;

    /**
     * App-specific version. Ideally a build version or NPM package vewrsion from the host application.
     */
    version?: string;
    /**
     * @deprecated All ARIA events log to the PROD tenant, so use `environment` instead for the version of the host application.
     */
    ariaEnvironment?: ISharedAriaEnvironment;
  };

/**
 * Telemetry handler which bakes in the configuration for using the Shared ARIA tenant.
 */
export class SharedAriaTelemetryHandler extends AriaTelemetryHandler {
  constructor(params: ISharedAriaTelemetryHandlerParams) {
    const {
      userAuthToken,
      userId
    } = params;

    const tokenClaims = userAuthToken && getTokenClaims(userAuthToken);

    const { context = {} } = params;

    const {
      clientName,
      correlationId,
      environment = params.ariaEnvironment || 'prod',
      language = context.Market,
      scenario = 'Unknown',
      version = 'Unknown',
      ariaLoader = async () => (await import('./AriaWrapper')).aria
    } = params;

    const userAccountType = tokenClaims && tokenClaims.puid ? 'Business' : 'Unknown';
    const appName = tokenClaims && tokenClaims.app_displayname || 'Unknown';
    const appId = tokenClaims && tokenClaims.appid || 'Unknown';
    const userIdForTelemetry = getUserIdForTelemetry(userId, tokenClaims, userAccountType);
    const isAuthenticated = userIdForTelemetry ? 1 : 0;
    const siteSubscriptionId = tokenClaims && tokenClaims.tid || 'Unknown';

    super({
      ariaLoader,
      context: {
        AccountType: userAccountType,
        AppName: appName,
        AppId: appId,
        ClientName: clientName,
        CorrelationId: correlationId,
        Environment: environment,
        IsAuthenticated: isAuthenticated,
        Market: language,
        Scenario: scenario,
        Session: Guid.generate(),
        SiteSubscriptionId: siteSubscriptionId,
        UserId: userIdForTelemetry,
        Version: version,
        Workload: 'SharedControls',
        ...context
      },
      tenantToken: ARIA_TOKEN
    });
  }
}

interface ITokenClaims {
  puid?: string;
  app_displayname?: string;
  appid?: string;
  tid?: string;
}

function getTokenClaims(authToken: string): ITokenClaims | undefined {
  try {
    const claimsPart = authToken.split('.')[1];
    if (claimsPart) {
      return JSON.parse(atob(claimsPart.replace(/-/g, '+').replace(/_/g, '/'))) as ITokenClaims;
    }
  } catch (e) {
    return undefined;
  }
}

function getUserIdForTelemetry(userId: string | undefined, tokenClaims: ITokenClaims | undefined | '', userAccountType: string): string {
  if (userId) {
    return userId;
  } else {
    if (userAccountType === 'Business') {
      return tokenClaims && tokenClaims.puid || '';
    } else {
      return '';
    }
  }
}
